import { create } from "zustand";
import { UserFormSchema } from "../../../common/components/user-inv-modal/api/schema";
import { FacilityModel, ProductModel } from "../../facilities/api/types";
import { UserModel } from "../../users-facility/api/types";

export interface IUsersState {
  pageNumber: number;
  setPageNumber: (val?: number) => void;

  pageSize: number;
  setPageSize: (val?: number) => void;

  searchText?: string;
  setSearchText: (val?: string) => void;

  isEditModal: boolean;
  setIsEditModal: (val: boolean) => void;

  selectedFacilityCode?: string;
  setSelectedFacilityCode: (val?: string) => void;

  selectedProductCode?: string;
  setSelectedProductCode: (val?: string) => void;

  isAddModalOpen: boolean;
  setIsAddModal: (val: boolean) => void;

  currentStep: number;
  setCurrentStep: (val?: number) => void;

  selectedUser?: UserFormSchema;
  setSelectedUser: (user: UserFormSchema | undefined) => void;

  pendingCreateUser?: Partial<UserFormSchema>;
  setPendingCreateUser: (user?: Partial<UserFormSchema>) => void;

  selectedFacilities: FacilityModel[];
  setSelectedFacilities: (val: FacilityModel) => void;

  selectedProducts: ProductModel[];
  setSelectedProducts: (val: ProductModel) => void;

  anchorEl: null | HTMLElement;
  setAnchorEl: (val: null | HTMLElement) => void;

  activeRowId: null | string;
  setActiveRowId: (val: undefined | string) => void;

  activeUser: undefined | UserModel;
  setActiveUser: (val: UserModel | undefined) => void;

  isResetPassModalOpen: boolean;
  setResetPassModal: (val: boolean) => void;

  isConfirmPassModalOpen: boolean;
  setConfirmPassModal: (val: boolean) => void;

  isUserCredentialsSent: boolean;
  setUserCredentialsSentModal: (val: boolean) => void;

  successMessage: string | undefined;
  setSuccessMessage: (val: string | undefined) => void;

  isLockUserModalOpen: boolean;
  setLockUserModal: (val: boolean) => void;

  isRemoveUserModalOpen: boolean;
  setRemoveUserModal: (val: boolean) => void;

  isManageAccessModalOpen: boolean;
  setManageAccessModal: (val: boolean) => void;

  setRole: (val: { role: number; product: ProductModel }) => void;
  setRemoveAllSelectedProductsAndFacilities: () => void;
  setPreSelectedFacilitiesAndProducts: (val: {
    facilities: FacilityModel[];
    products: ProductModel[];
  }) => void;
  resetState: () => void;

  showNewPassword: boolean;
  setShowNewPassword: (show: boolean) => void;

  showConfirmPassword: boolean;
  setShowConfirmPassword: (show: boolean) => void;

  resetSearch: () => void;
}
const initialData = {
  pageNumber: 1,
  pageSize: 10,
  searchText: undefined,
  selectedFacilityCode: undefined,
  selectedProductCode: undefined,
  isAddModalOpen: false,
  currentStep: 0,
  selectedUser: undefined,
  pendingCreateUser: undefined,
  anchorEl: null,
  activeRowId: null,
  activeUser: undefined,
  isResetPassModalOpen: false,
  isConfirmPassModalOpen: false,
  isUserCredentialsSent: false,
  isLockUserModalOpen: false,
  isRemoveUserModalOpen: false,
  isEditModal: false,

  selectedFacilities: [],
  selectedProducts: [],
};

const useUsersStore = create<IUsersState>((set) => ({
  ...initialData,

  successMessage: undefined,
  setIsEditModal: (isEditModal) => set({ isEditModal }),

  setRemoveUserModal: (isRemoveUserModalOpen) => set({ isRemoveUserModalOpen }),
  setLockUserModal: (isLockUserModalOpen) => set({ isLockUserModalOpen }),
  setSuccessMessage: (successMessage) => set({ successMessage }),
  setResetPassModal: (isResetPassModalOpen) => set({ isResetPassModalOpen }),
  setConfirmPassModal: (isConfirmPassModalOpen) =>
    set({ isConfirmPassModalOpen }),
  setUserCredentialsSentModal: (isUserCredentialsSent) =>
    set({ isUserCredentialsSent }),
  setActiveUser: (activeUser) => set({ activeUser }),
  setAnchorEl: (anchorEl) => set({ anchorEl }),
  setActiveRowId: (activeRowId) => set({ activeRowId }),
  setSelectedProducts: (val) =>
    set((state) => {
      const isPresent = state.selectedProducts.some((p) => p.id === val.id);
      return {
        selectedProducts: isPresent
          ? state.selectedProducts.filter((p) => p.id !== val.id)
          : [...state.selectedProducts, { ...val, role: 0 }],
      };
    }),

  setSelectedFacilities: (val) =>
    set((state) => {
      const isPresent = state.selectedFacilities.some((f) => f.id === val.id);
      const newFacilities = isPresent
        ? state.selectedFacilities.filter((f) => f.id !== val.id)
        : [...state.selectedFacilities, val];

      // If a facility is being removed, filter out its products
      const newProducts = isPresent
        ? state.selectedProducts.filter((p) => p.facilityId !== val.id)
        : state.selectedProducts;
      return {
        selectedFacilities: newFacilities,
        selectedProducts: newProducts,
      };
    }),
  setRole: (val) =>
    set((state) => ({
      selectedProducts: state.selectedProducts.map((product) =>
        product.id === val.product.id
          ? { ...product, role: val.role }
          : product,
      ),
    })),

  setSelectedUser: (user: UserFormSchema | undefined) =>
    set({ selectedUser: user }),
  setPendingCreateUser: (pendingCreateUser) => set({ pendingCreateUser }),
  setCurrentStep: (currentStep) => set({ currentStep }),
  setIsAddModal: (isAddModalOpen) => set({ isAddModalOpen }),
  setSelectedFacilityCode: (selectedFacilityCode) =>
    set({ selectedFacilityCode }),
  setSelectedProductCode: (selectedProductCode) => set({ selectedProductCode }),
  setSearchText: (searchText) => set({ searchText }),
  setPageSize: (pageSize) => set({ pageSize }),
  setPageNumber: (pageNumber) => set({ pageNumber }),
  setRemoveAllSelectedProductsAndFacilities: () =>
    set((state) => ({
      selectedFacilities: [],
      selectedProducts: [],
    })),
  setPreSelectedFacilitiesAndProducts: (val: {
    facilities: FacilityModel[];
    products: ProductModel[];
  }) =>
    set((state) => ({
      selectedFacilities: val.facilities,
      selectedProducts: val.products,
    })),
  resetState: () => set(initialData),

  showNewPassword: false,
  setShowNewPassword: (showNewPassword: boolean) => set({ showNewPassword }),

  showConfirmPassword: false,
  setShowConfirmPassword: (showConfirmPassword: boolean) =>
    set({ showConfirmPassword }),

  isManageAccessModalOpen: false,
  setManageAccessModal: (isManageAccessModalOpen: boolean) =>
    set({ isManageAccessModalOpen }),

  resetSearch: () =>
    set(() => ({
      ...initialData,
      searchText: undefined,
      pageNumber: 1,
    })),
}));

export default useUsersStore;
